import React, { useEffect, useState } from 'react';
import { X, Sparkles, Zap } from 'lucide-react';
import api from '../lib/api';
import { openUpgradeSheet } from './subscription/UpgradeSheet';

const DISMISS_HOURS = 24;

/**
 * Welcome / upgrade banner shown on login for FREE-plan orgs.
 *
 * "Hi welcome — you're on the Free plan (X of N bills used this month). Upgrade
 * to a plan or top up your bill credits to process more bills."
 *
 * Dismissing hides it for the rest of the session and re-shows only after 24h
 * (tracked per account in localStorage).
 */
export default function FreePlanBanner({ account }) {
  const [snap, setSnap] = useState(null);
  const [hidden, setHidden] = useState(true);

  const dismissKey = account?.id ? `freeplan_banner_dismissed_at:${account.id}` : null;

  useEffect(() => {
    if (!account?.id) return;
    let cancelled = false;
    api.get('/subscription/current', { params: { account_id: account.id } })
      .then(({ data }) => {
        if (cancelled) return;
        setSnap(data);
        const planId = data?.plan?.id;
        if (planId !== 'free') { setHidden(true); return; }
        // Respect a recent dismissal (< 24h)
        let recentlyDismissed = false;
        try {
          const ts = dismissKey ? Number(localStorage.getItem(dismissKey) || 0) : 0;
          recentlyDismissed = ts && (Date.now() - ts) < DISMISS_HOURS * 3600 * 1000;
        } catch { /* ignore */ }
        setHidden(!!recentlyDismissed);
      })
      .catch(() => { /* non-fatal */ });
    return () => { cancelled = true; };
  }, [account?.id, dismissKey]);

  if (hidden || !snap || snap?.plan?.id !== 'free') return null;

  const bill = snap?.usage?.bill_credits || {};
  const allowance = bill.allowance ?? 5;
  const consumed = bill.consumed ?? 0;
  const purchased = Math.max(0, Math.round(bill.purchased_credits ?? 0));
  const usedUp = (bill.remaining ?? (allowance - consumed)) <= 0 && purchased <= 0;

  const dismiss = () => {
    try { if (dismissKey) localStorage.setItem(dismissKey, String(Date.now())); } catch { /* ignore */ }
    setHidden(true);
  };

  return (
    <div
      data-testid="free-plan-banner"
      className={`w-full border-b ${usedUp ? 'bg-rose-50 border-rose-200' : 'bg-emerald-50 border-emerald-200'}`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 md:px-6 py-2">
        <Sparkles className={`h-4 w-4 shrink-0 ${usedUp ? 'text-rose-600' : 'text-emerald-600'}`} />
        <p className={`min-w-0 flex-1 text-[13px] leading-snug ${usedUp ? 'text-rose-800' : 'text-emerald-900'}`}>
          {usedUp ? (
            <><span className="font-semibold">{"You've reached your Free plan limit"}</span> ({consumed}/{allowance} free bill credits used this month — 1 credit = one account on a bill). New bills are held in the queue — upgrade or buy bill credits to keep processing.</>
          ) : (
            <><span className="font-semibold">Hi, welcome!</span>{" You're on the "}<span className="font-semibold">Free plan</span> — {Math.max(0, allowance - consumed)} of {allowance} free bill credits left this month{purchased > 0 ? ` (+${purchased} pre-paid)` : ''}. <span className="opacity-80">1 credit = one account on a bill.</span> Upgrade or top up to process more.</>
          )}
        </p>
        <button
          type="button"
          onClick={() => openUpgradeSheet('credits', account?.id)}
          data-testid="free-plan-banner-buy"
          className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-md border border-emerald-300 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
        >
          <Zap className="h-3.5 w-3.5" /> Buy bill credits
        </button>
        <button
          type="button"
          onClick={() => openUpgradeSheet('plans', account?.id)}
          data-testid="free-plan-banner-upgrade"
          className="shrink-0 inline-flex items-center gap-1 rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"
        >
          Upgrade plan
        </button>
        <button
          type="button"
          onClick={dismiss}
          data-testid="free-plan-banner-close"
          aria-label="Dismiss"
          className={`shrink-0 rounded p-1 ${usedUp ? 'text-rose-500 hover:bg-rose-100' : 'text-emerald-600 hover:bg-emerald-100'}`}
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
